/**
 * Footer Component - MyyVC Website (World-Class UI/UX Design)
 * 
 * Professional footer with modern design:
 * - Clean layout with company information
 * - Social media links with hover effects
 * - Responsive design for all devices
 * - Accessibility optimized
 * - Professional blue/white color palette
 * 
 * @author World-Class UI/UX Designer
 * @version 2.0 - Modern Professional Design
 */

import {
  Extra,
  FooterContainer,
  FooterSection,
  LogoContainer,
  NavLink,
} from "./styles";

import Container from "../../common/Container";
import { Row, Col } from "antd";
import { SvgIcon } from "../../common/SvgIcon";
import { TFunction } from "react-i18next";

interface SocialLinkProps {
  href: string;
  src: string;
  label: string;
}

const Footer = ({ t }: { t?: TFunction }) => {

  const SocialLink = ({ href, src, label }: SocialLinkProps) => {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        key={src}
        aria-label={label}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: "44px",
          height: "44px",
          background: "var(--gray-100)",
          borderRadius: "var(--radius-lg)",
          transition: "all var(--transition-normal)",
          cursor: "pointer"
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.background = "var(--blue-600)";
          e.currentTarget.style.transform = "translateY(-2px)";
          e.currentTarget.style.boxShadow = "var(--shadow-lg)";
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.background = "var(--gray-100)";
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "none";
        }}
      >
        <SvgIcon src={src} width="22px" height="22px" />
      </a>
    );
  };

  return (
    <>
      <FooterSection>
        <Container>
          <div style={{
            padding: "var(--space-16) 0 var(--space-12) 0"
          }}>
            <Row gutter={[32, 32]} justify="space-between">
              {/* Company Info */}
              <Col xs={24} sm={24} md={8} lg={8}>
                <div style={{ marginBottom: "var(--space-6)" }}>
                  <NavLink to="/">
                    <LogoContainer>
                      <SvgIcon
                        src="venture.png"
                        alt="Myy Venture Capital"
                        width="101px"
                        height="64px"
                      />
                    </LogoContainer>
                  </NavLink>
                </div>
                <p style={{
                  fontSize: "var(--text-base)",
                  color: "var(--gray-600)",
                  lineHeight: "1.6",
                  marginBottom: "var(--space-6)",
                  maxWidth: "280px"
                }}>
                  Building tomorrow's unicorns from universities through strategic partnerships and early-stage investment.
                </p>
                
                {/* Contact Info */}
                <div style={{
                  fontSize: "var(--text-sm)",
                  color: "var(--gray-500)",
                  lineHeight: "1.5"
                }}>
                  <div style={{ marginBottom: "var(--space-2)" }}>
                    <strong>Email:</strong>{" "}
                    <a href="mailto:aderiyebiola@gmail.com" style={{ color: "inherit" }}>aderiyebiola@gmail.com</a>
                  </div>
                  <div>
                    <strong>Location:</strong> United Kingdom
                  </div>
                </div>
              </Col>

              {/* Quick Links */}
              <Col xs={12} sm={12} md={4} lg={4}>
                <h4 style={{
                  fontSize: "var(--text-lg)",
                  fontWeight: "700",
                  color: "var(--blue-900)",
                  marginBottom: "var(--space-4)"
                }}>
                  Company
                </h4>
                <div style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "var(--space-3)"
                }}>
                  {[
                    { label: "About Us", href: "/about" },
                    { label: "Partnerships", href: "/partnerships" },
                    { label: "Challenges", href: "/challenges" },
                    { label: "Careers", href: "/careers" }
                  ].map((link, index) => (
                    <a
                      key={index}
                      href={link.href}
                      style={{
                        fontSize: "var(--text-base)",
                        color: "var(--gray-600)",
                        textDecoration: "none",
                        transition: "color var(--transition-fast)"
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.color = "var(--blue-600)";
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.color = "var(--gray-600)";
                      }}
                    >
                      {link.label}
                    </a>
                  ))}
                </div>
              </Col>

              {/* Resources */}
              <Col xs={12} sm={12} md={4} lg={4}>
                <h4 style={{
                  fontSize: "var(--text-lg)",
                  fontWeight: "700",
                  color: "var(--blue-900)",
                  marginBottom: "var(--space-4)"
                }}>
                  Resources
                </h4>
                <div style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "var(--space-3)"
                }}>
                  {[
                    { label: "Portfolio", href: "/#impact" },
                    { label: "Case Studies", href: "/#case-studies" },
                    { label: "Contact", href: "/#contact" }
                  ].map((link, index) => (
                    <a
                      key={index}
                      href={link.href}
                      style={{
                        fontSize: "var(--text-base)",
                        color: "var(--gray-600)",
                        textDecoration: "none",
                        transition: "color var(--transition-fast)"
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.color = "var(--blue-600)";
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.color = "var(--gray-600)";
                      }}
                    >
                      {link.label}
                    </a>
                  ))}
                </div>
              </Col>

              {/* Social Media */}
              <Col xs={24} sm={24} md={6} lg={6}>
                <h4 style={{
                  fontSize: "var(--text-lg)",
                  fontWeight: "700",
                  color: "var(--blue-900)",
                  marginBottom: "var(--space-4)"
                }}>
                  Follow Us
                </h4>
                <p style={{
                  fontSize: "var(--text-base)",
                  color: "var(--gray-600)",
                  marginBottom: "var(--space-6)",
                  lineHeight: "1.5"
                }}>
                  Stay updated with our latest news, portfolio companies, and innovation challenges.
                </p>
                <FooterContainer>
                  <SocialLink
                    href="https://twitter.com/_myyventureCap"
                    src="twitter.svg"
                    label="Follow us on Twitter"
                  />
                  <SocialLink
                    href="https://www.linkedin.com/in/myy-venture-capital"
                    src="linkedin.svg"
                    label="Connect with us on LinkedIn"
                  />
                  <SocialLink
                    href="https://www.instagram.com/myyventurecapital"
                    src="instagram.svg"
                    label="Follow us on Instagram"
                  />
                  <SocialLink
                    href="https://www.youtube.com/channel/UCpRDZJlwe1JkC8x0UwKvp7w"
                    src="youtube.svg"
                    label="Subscribe to our YouTube channel"
                  />
                  <SocialLink
                    href="https://www.tiktok.com/@myyventurecapital?is_from_webapp=1&sender_device=pc"
                    src="tiktok.svg"
                    label="Follow us on TikTok"
                  />
                </FooterContainer>
              </Col>
            </Row>
          </div>
        </Container>
      </FooterSection>
      
      {/* Bottom Bar */}
      <Extra>
        <Container border={false}>
          <Row
            justify="space-between"
            align="middle"
            style={{
              padding: "var(--space-6) 0",
              borderTop: "1px solid var(--gray-200)"
            }}
          >
            <Col xs={24} sm={12}>
              <p style={{
                fontSize: "var(--text-sm)",
                color: "var(--gray-500)",
                margin: 0,
                textAlign: "center"
              }}>
                © {new Date().getFullYear()} Myy Venture Capital. All rights reserved.
              </p>
            </Col>
            <Col xs={24} sm={12}>
              <div style={{
                display: "flex",
                justifyContent: "center",
                gap: "var(--space-6)",
                fontSize: "var(--text-sm)"
              }}>
                <a
                  href="/privacy"
                  style={{
                    color: "var(--gray-500)",
                    textDecoration: "none",
                    transition: "color var(--transition-fast)"
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.color = "var(--blue-600)";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.color = "var(--gray-500)";
                  }}
                >
                  Privacy Policy
                </a>
                <a
                  href="/terms"
                  style={{
                    color: "var(--gray-500)",
                    textDecoration: "none",
                    transition: "color var(--transition-fast)"
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.color = "var(--blue-600)";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.color = "var(--gray-500)";
                  }}
                >
                  Terms of Service
                </a>
              </div>
            </Col>
          </Row>
        </Container>
      </Extra>
    </>
  );
};

export default Footer;